/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useMutation } from '../../../../Hooks/API/useMutation';
import { isVoucherValid, voucherMessage, previewData } from '../../../../Signals/formStateSignal';
import useErrorCodeMapping from '../../../../Errors/useErrorCodeMapping';
import { useCheckoutStore } from '../../../../Hooks/Zustand/useCheckoutStore';

/**
 * Custom usePreviewForm hook
 * @returns {{isLoading, rateBundleData}}
 */
export function usePreviewForm() {
  const {
    values: {
      studioId,
      rateBundleTerm,
      firstName,
      dateOfBirth,
      lastName,
      email,
      street,
      houseNumber,
      city,
      postCode,
      countryCode,
      paymentChoice,
      voucherCode,
      referralCode,
    },
    validateField,
  } = useFormikContext();

  const defaultPreviewData = useRef(null);
  const initialPreviewDataSet = useRef(false);
  const isComponentMounted = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const mutation = useMutation('previewContract');
  const { updateFormDataValue } = useCheckoutStore();
  const { setFieldValue } = useFormikContext();
  const { getErrorMessage } = useErrorCodeMapping();

  /**
     * Whenever the voucher signal value changes, validate form field
     */
  useEffect(() => {
    if (voucherCode && !isLoading) {
      validateField('voucherCode');
    }
    /* eslint-disable-next-line */
    }, [voucherMessage.value, isVoucherValid.value, validateField, isLoading])

  /**
     * Handle success when preview data got updated
     * @param response
     * @param variables request payload
     * @param isResetCall
     */
  const onSuccess = useCallback((response, requestArguments) => {
    if (requestArguments.voucherCode) {
      isVoucherValid.value = true;
      voucherMessage.value = '';
    }

    if (!initialPreviewDataSet.current) {
      initialPreviewDataSet.current = true;
      defaultPreviewData.current = response;
    }

    previewData.value = response;
    setIsLoading(false);
  }, []);

  /**
     * Handle error
     * @param error
     */
  const onError = useCallback(
    (error) => {
      const { message, errorCodes } = error.response.data;
      if (
        errorCodes.includes('VOUCHER_VALIDATION_FAILED_INVALID_CODE')
                || errorCodes.includes('VOUCHER_VALIDATION_FAILED_NOT_REDEEMABLE_HERE')
                || errorCodes.includes('VOUCHER_VALIDATION_FAILED_NOT_IN_VALIDITY_PERIOD')
                || errorCodes.includes('VOUCHER_VALIDATION_FAILED_NOT_ALLOWED_FOR_RATE')
      ) {
        isVoucherValid.value = false;
        voucherMessage.value = getErrorMessage(message, errorCodes);
        setFieldValue('voucherCode', '').then(() => {
          previewData.value = initialPreviewDataSet.current;
          updateFormDataValue('voucherCode', '');
        });
        setTimeout(() => {
          isVoucherValid.value = true;
        }, 2500);
      }
      setIsLoading(false);
    },
    [getErrorMessage, setFieldValue, updateFormDataValue],
  );

  /**
     * Return preview parameters
     */
  const getPreviewParameters = useCallback(
    () => ({
      studioId,
      voucherCode,
      contract: {
        rateBundleTermId: rateBundleTerm.id,
        startDate: rateBundleTerm.defaultContractStartDate,
        preuseDate: rateBundleTerm.contractStartDateOfUse,
      },
      customer: {
        firstname: firstName,
        lastname: lastName,
        dateOfBirth: dayjs(dateOfBirth).format('YYYY-MM-DD'),
        referralCode,
        email,
        street,
        houseNumber,
        city,
        zipCode: postCode,
        countryCode,
        paymentChoice,
      },
    }),
    [
      city,
      countryCode,
      dateOfBirth,
      email,
      firstName,
      houseNumber,
      lastName,
      paymentChoice,
      postCode,
      rateBundleTerm.defaultContractStartDate,
      rateBundleTerm.contractStartDateOfUse,
      rateBundleTerm.id,
      referralCode,
      street,
      studioId,
      voucherCode,
    ],
  );

  /**
     * Preview contract data
     */
  useEffect(() => {
    if (!isComponentMounted.current) {
      isComponentMounted.current = true;
      setIsLoading(true);
      mutation.mutate(getPreviewParameters(), {
        onSuccess,
        onError,
      });
    }
  }, [getPreviewParameters, mutation, onError, onSuccess]);

  /**
     * Update preview data
     */
  const updatePreviewData = useCallback(() => {
    setIsLoading(true);
    mutation.mutate(getPreviewParameters(), {
      onSuccess,
      onError,
    });
  }, [getPreviewParameters, mutation, onError, onSuccess]);

  return {
    isLoading,
    updatePreviewData,
    isVoucherValid,
    previewData,
  };
}
